import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CacheKeys } from '@/lib/cache'
import { EnvelopeKind, SendBorDialog } from './SendBorDialog'

const SEND_MENU_ITEMS: { kind: EnvelopeKind; label: string }[] = [
  { kind: 'regular', label: 'BOR' },
  { kind: 'canal', label: 'Canal BOR' },
  { kind: 'rocklake_payment', label: 'Rocklake Payment Authorization' },
  { kind: 'rocklake_credit_card', label: 'Rocklake Credit Card Authorization' },
]

interface SendAuthorizationLinkMenuProps {
  orgId: string
  /** After a link is copied or sent, beside the envelope list's own refresh. */
  onSent?: () => void
  /** `sm` sits in a card header, as on the Documents tab. */
  size?: 'sm' | 'default'
  /** Which edge the menu lines up with: `end` under a button on the right. */
  align?: 'start' | 'end'
}

/** Picks an authorization to sign, then copies or emails its link — the Documents tab's envelopes and Intake both. */
export function SendAuthorizationLinkMenu({
  orgId,
  onSent,
  size = 'sm',
  align = 'end',
}: SendAuthorizationLinkMenuProps) {
  const queryClient = useQueryClient()
  const [activeKind, setActiveKind] = useState<EnvelopeKind | null>(null)

  function handleSuccess() {
    void queryClient.invalidateQueries({ queryKey: [CacheKeys.ORG_ENVELOPES, orgId] })
    onSent?.()
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant="outline">
            Send authorization link
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align}>
          {SEND_MENU_ITEMS.map(({ kind, label }) => (
            <DropdownMenuItem key={kind} onClick={() => setActiveKind(kind)}>
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {activeKind && (
        <SendBorDialog
          orgId={orgId}
          kind={activeKind}
          onSuccess={handleSuccess}
          open
          onOpenChange={(isOpen) => !isOpen && setActiveKind(null)}
        />
      )}
    </>
  )
}
